'use client';
import {
  Archive,
  ArrowSquareRight,
  DotsVertical,
  Download01,
  File02,
  FileDownload02,
  Folder,
  Home05,
  InfoCircle,
  LinkBroken01,
  Mail01,
  PenTool02,
  Share06,
  SlashCircle01
} from '@ethsign/icons';
import { Alert, AlertDescription, Button, Dropdown, ScrollArea } from '@ethsign/ui';
import { IDropdownItem } from '@ethsign/ui/dist/Menu';
import { shortenWalletAddress } from '@ethsign/utils-web';

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

import {
  CheckItem,
  ColorAlertIcon,
  ColorErrorIcon,
  ColorRemoveIcon,
  ConfirmModal,
  DecryptContractModal,
  FileUploadArea,
  FolderCircleIcon,
  Loading,
  ProgressBar,
  StatusTag
} from '@/components';
import { CopyText } from '@/components/CopyText';
import { DetailModal } from '@/components/Modal/DetailModal';
import { InviteViewersModal } from '@/components/Modal/InviteViewersModal';
import { ShareContractModal } from '@/components/Modal/ShareContractModal';
import { useRouter, useSearchParams } from '@/hooks';
import { useUserInfo } from '@/hooks/useUserInfo';
import { ContractItem, Contracts, ContractType } from '@/types';
import { dateFormat, reportGAEvent, stringifyQueryString } from '@/utils';

import { useViewModel } from '@/utils/bizify';

import { PageCtrl } from './PageCtrl';
import { ContractCard, ContractTable, SideNavigation, TopArea } from './components';

const Page = () => {
  const { folderId } = useSearchParams();
  const vm = useViewModel(PageCtrl);

  const router = useRouter();
  const { user } = useUserInfo();
  const vmData = vm.$useSnapshot();
  const {
    openVoidModal,
    openDeactivateModal,
    openHideModal,
    openMoveModal,
    selectedContracts,
    checkedFolder,
    folders,
    contracts,
    loading,
    contractsLoading,
    passwordModalOpen,
    currentContract,
    contractsQuery,
    topContracts,
    openDetailModal,
    password,
    passwordModalType,
    shareModalOpen,
    shareType,
    openInviteViewersModal
  } = vmData;

  const columns = useMemo(
    () => [
      {
        header: 'Contract Name',
        accessorKey: 'title',
        cell: ({ row }: any) => {
          const data = row.original;
          return (
            <div className="pl-8 relative flex flex-col justify-center">
              <img
                className="w-6 absolute left-0 top-[6px]"
                src={
                  data.contractType === ContractType.contract
                    ? 'https://sign-public-cdn.s3.us-east-1.amazonaws.com/signature-next/file_240723064339.webp'
                    : 'https://sign-public-cdn.s3.us-east-1.amazonaws.com/signature-next/bulk_240723064339.webp'
                }
              ></img>
              <div
                className="text-sm font-medium max-w-[260px] flex flex-row items-center justify-start"
                title={data.title}
              >
                <span className=" truncate ">{data.title}</span>
                {data.contractType !== ContractType.contract && (
                  <span className="inline-block ml-1 bg-darkMatter-25 text-darkMatter-500 text-xs font-mediu px-2 py-[2px] rounded-[11px]">
                    {data.currentSigned}
                  </span>
                )}
              </div>
              <div className="mt-1 text-xs font-normal flex flex-row items-center ">
                Last Modified: {dateFormat(data.date)}
                {data.folderName && (
                  <span className="inline-block w-[52px] truncate ml-1 cursor-pointer" title={data.folderName}>
                    in {data.folderName}
                  </span>
                )}
              </div>
            </div>
          );
        }
      },
      {
        header: 'Sent by',
        accessorKey: 'sentBy',
        cell: ({ row }: any) => {
          const data: ContractItem = row.original;
          const { sentBy, sentByName } = data;
          return (
            <div className="flex flex-col justify-center">
              <div className="text-sm font-medium">{sentByName}</div>
              <CopyText
                className="mt-1"
                displayText={<div className="text-xs font-normal">{shortenWalletAddress(sentBy, 'normal')}</div>}
                text={sentBy}
                buttonSize={16}
              ></CopyText>
            </div>
          );
        }
      },
      {
        header: 'Signing Status',
        accessorKey: 'signers',
        cell: ({ row }: any) => {
          const data = row.original;
          const { totalSigning, currentSigned, contractType } = data;
          return (
            <div className="flex flex-row items-center">
              <div className="w-[122px] mr-3">
                <ProgressBar value={(currentSigned / totalSigning) * 100}></ProgressBar>
              </div>
              <span className="text-sm font-medium text-gray-500">
                {currentSigned}
                {contractType === ContractType.contract ? `/${totalSigning}` : ''} Signed
              </span>
            </div>
          );
        }
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ getValue }: any) => {
          return (
            <div className="inline-block">
              <StatusTag hasDot={false} status={getValue()}></StatusTag>
            </div>
          );
        }
      },
      {
        header: 'Action',
        accessorKey: 'status',
        cell: ({ row }: any) => {
          const data = row.original;
          const { status, id, contractType, sentBy } = data;
          const isVisualContract = contractType === ContractType.visual_contract;
          const isLinkSignContract = contractType === ContractType.link_sign_contract;
          const isSender = user?.address === sentBy || user?.displayAddress === sentBy;
          let query = stringifyQueryString({
            type: isSender && (isVisualContract || isLinkSignContract) ? 'view' : ''
          });
          query = query ? `?${query}` : '';
          return (
            <Link to={`/contract/${id}${query}`}>
              {status === 'wait_me' && (
                <Button variant="secondary" className="border border-primary-50 min-w-[131px]">
                  Sign Contract
                </Button>
              )}
              {status !== 'wait_me' && (
                <Button variant="secondary-gray" className={isVisualContract ? 'min-w-[160px]' : 'min-w-[131px]'}>
                  {isVisualContract ? 'View All Contracts' : 'View Contract'}
                </Button>
              )}
            </Link>
          );
        }
      },
      {
        header: '',
        accessorKey: 'status',
        cell: ({ row }: any) => {
          const data: ContractItem = row.original;
          const isVisualContract = data.contractType === ContractType.visual_contract;
          const isLinkSignContract = data.contractType === ContractType.link_sign_contract;
          const isSender = user?.address === data.sentBy || user?.displayAddress === data.sentBy;
          const isVoid = data.clientStatus === 'void';
          let items: IDropdownItem[] = [
            {
              icon: <Download01 size={20} color="#101828"></Download01>,
              label: isVisualContract ? 'Download All Contracts' : 'Download Contract',
              disabled:
                (isVisualContract ? data.currentSigned < 1 : data.status !== 'completed') || data.totalSigning > 100,
              onClick: () => {
                if (data.enableEncryption) {
                  vm.setCurrentContract(data);
                  vm.setPassWordModalOpen(true);
                } else {
                  vm.onDownload(data);
                }
              }
            },
            {
              icon: <Share06 size={20} color="#101828"></Share06>,
              label: 'Share to Viewers',
              onClick: () => {
                vm.setCurrentContract(data);
                vm.setShareType('viewer');
                if (data.enableEncryption) {
                  vm.setPassWordModalOpen(true);
                  vm.setPasswordModalType('share');
                } else {
                  vm.setCurrentContract(data);
                  vm.setShareModalOpen(true);
                }
              }
            },
            {
              icon: <File02 size={20} color="#101828"></File02>,
              label: 'Contract Details',
              onClick: () => {
                vm.setCurrentContract(data);
                vm.setOpenDetailModal(true);
              }
            },
            {
              icon: <ArrowSquareRight size={20} color="#101828"></ArrowSquareRight>,
              label: 'Move to...',
              onClick: () => {
                vm.onMove([data]);
              }
            },
            {
              icon: <Archive size={20} color="#101828"></Archive>,
              label: 'Move to Trash',
              onClick: () => {
                vm.onHide([data]);
              }
            }
          ];
          if (data?.contractType === ContractType.contract) {
            items.splice(2, 0, {
              icon: <Mail01 size={20} color="#101828"></Mail01>,
              label: 'Share via Email',
              onClick: () => {
                vm.setCurrentContract(data);
                vm.setInviteViewersModal(true);
              }
            });
          }
          if (isVisualContract || isLinkSignContract) {
            if (isSender) {
              items.unshift({
                icon: <LinkBroken01 size={20} color="#101828"></LinkBroken01>,
                label: 'Deactivate Signing Link',
                disabled: data.status === 'completed' || data.status === 'expired',
                onClick: () => {
                  vm.setOpenDeactivateModal(true);
                  vm.setCurrentContract(data);
                }
              });
              items.splice(2, 0, {
                icon: <FileDownload02 size={20} color="#101828"></FileDownload02>,
                label: 'Export Signing Events',
                disabled: isVisualContract ? data.currentSigned < 1 : data.status !== 'completed',
                onClick: () => {
                  vm.exportSigners(data.id, data.title);
                }
              });
            }
            items = [
              {
                icon: <PenTool02 size={20} color="#101828"></PenTool02>,
                label: 'Contract Signing Link',
                disabled: data.status === 'completed' || data.status === 'expired',
                onClick: () => {
                  vm.setShareType('signer');
                  vm.setCurrentContract(data);
                  if (data.enableEncryption) {
                    vm.setPassWordModalOpen(true);
                    vm.setPasswordModalType('share');
                  } else {
                    vm.setCurrentContract(data);
                    vm.setShareModalOpen(true);
                  }
                }
              },

              ...items
            ];
          }
          if (
            data.contractType === ContractType.contract &&
            data.clientStatus !== 'completed' &&
            data.clientStatus !== 'expired' &&
            !isVoid &&
            isSender
          ) {
            items.push({
              icon: <SlashCircle01 size={20} color="#101828"></SlashCircle01>,
              label: 'Void Contract',
              onClick: () => {
                vm.setVoidModalOpen(true);
                vm.setCurrentContract(data);
              }
            });
          }
          return (
            <div>
              <Dropdown items={items}>
                <DotsVertical
                  color="#98A2B3"
                  size={20}
                  onClick={(e: any) => {
                    console.log(e);
                    e.stopPropagation();
                  }}
                ></DotsVertical>
              </Dropdown>
            </div>
          );
        }
      }
    ],
    []
  );

  useEffect(() => {
    vm.getFolders();
    vm.getTopContracts({ folder: folderId });
  }, [folderId]);

  return (
    <div className="h-full flex flex-row w-full bg-white">
      <SideNavigation vm={vm} folderId={folderId} folders={folders}></SideNavigation>
      <div className="relative flex-1 h-full flex flex-col">
        <TopArea
          title={folders?.rows?.find((item: any) => item.id + '' === folderId)?.folderName || 'Manage Contracts'}
          subtitle="View, sign, manage, and download your contracts here."
          onUpload={() => {
            const id = `temp${Date.now()}`;
            reportGAEvent('pc_contract_createNewContractClick', {
              address: user!.address,
              folderId: folderId,
              tempId: id
            });
            router.push(`/prepare/${id}${folderId ? `?folderId=${folderId}` : ''}`);
          }}
        ></TopArea>
        <ScrollArea className="relative flex-1 h-full flex flex-col ">
          {contractsLoading && <Loading></Loading>}
          <div className="px-5 mt-5">
            {topContracts?.length === 3 ? (
              <div className="flex flex-row">
                {topContracts.map((item: any, i: number) => (
                  <ContractCard key={i} user={user} {...item}></ContractCard>
                ))}
              </div>
            ) : (
              contracts?.data &&
              contracts.data.length <= 3 && (
                <>
                  <Alert className="mb-5">
                    <InfoCircle size={20} color="#475467" />
                    <AlertDescription className="text-sm font-normal text-gray-600">
                      Sent contract files will be immediately encrypted for secure transmission. EthSign will have no
                      access to the contract&apos;s content, and it will be stored on-chain after all signers have
                      completed their signatures.
                    </AlertDescription>
                  </Alert>
                  <FileUploadArea
                    className={'!h-[284px]'}
                    mode="view2"
                    onUpload={async (file) => {
                      vm.onUpload(file, folderId, router, user!);
                    }}
                  ></FileUploadArea>
                </>
              )
            )}
          </div>
          <div className="px-5 my-5 flex-1 ">
            <ContractTable
              folderId={folderId}
              contractsQuery={contractsQuery}
              key={folderId}
              rowSelection={{ rowSelected: selectedContracts }}
              columns={columns}
              data={contracts as Contracts}
              onBatchMove={(selectedData: any) => {
                vm.onMove(selectedData);
              }}
              onBatchHide={(selectedData: any) => {
                vm.onHide(selectedData);
              }}
              onQuery={(data: any) => {
                vm.getContracts({ folder: folderId, ...data });
              }}
            ></ContractTable>
          </div>
        </ScrollArea>
      </div>
      <ConfirmModal
        className="!w-[400px]"
        loading={loading}
        onOpenChange={(open) => {
          vm.setOpenHideModal(open);
        }}
        icon={<ColorRemoveIcon />}
        type="warn"
        open={openHideModal}
        variant="destructive"
        title={`Hide ${selectedContracts.length > 1 ? selectedContracts.length + ' Contracts' : 'Contract'}`}
        content="Are you sure you want to hide this contract?You can still view the contract in the Hidden Contract tab."
        confirmText="Hide Contract"
        onConfirm={() => {
          vm.onHideConfirm(folderId);
        }}
      />
      <ConfirmModal
        className="!w-[433px]"
        onOpenChange={(open) => {
          vm.setOpenDeactivateModal(open);
        }}
        variant="primary"
        icon={<ColorAlertIcon />}
        type="warn"
        open={openDeactivateModal}
        title="Are you sure you want to deactivate the signing link of this contract? "
        content="Once deactivated, the contract link will no longer be available. The contract will be marked as complete and stored on chain."
        confirmText="Deactivate"
        onConfirm={() => {
          vm.deActiveContract(currentContract.id, folderId);
        }}
      />
      <ConfirmModal
        className="!w-[433px]"
        onOpenChange={(open) => {
          vm.setVoidModalOpen(open);
        }}
        variant="destructive"
        icon={<ColorErrorIcon />}
        type="warn"
        open={openVoidModal}
        title="Void Contract"
        content="Are you sure you want to void this contract?This action is irreversible and will cancel all associated terms."
        confirmText="Void"
        onConfirm={() => {
          vm.voidContract(currentContract.id, folderId);
        }}
      />
      <ConfirmModal
        className="!w-[480px]"
        loading={loading}
        onOpenChange={(open: boolean) => {
          vm.setOpenMoveModal(open);
          vm.setCheckedFolder({});
        }}
        icon={<FolderCircleIcon />}
        type="normal"
        open={openMoveModal}
        title={`Move ${
          selectedContracts.length > 1 ? selectedContracts.length + ' Contracts' : 'Contract'
        } to a folder`}
        content={
          <>
            <div>Choose your folder from the list</div>
            <ScrollArea className="max-w-full mt-5 h-[380px]">
              {[{ folderName: 'All Contracts' }, ...(folders?.rows as any[])].map((item, i) => (
                <CheckItem
                  disabled={selectedContracts.length === 1 && selectedContracts[0].folderId === item.id}
                  icon={
                    item.id === undefined ? <Home05 size={20} color="#344054" /> : <Folder size={20} color="#344054" />
                  }
                  checkedIcon={
                    item.id === undefined ? <Home05 color="#CD7134" size={20} /> : <Folder color="#CD7134" size={20} />
                  }
                  key={i}
                  name={item.folderName}
                  id={item.id}
                  checked={checkedFolder.id === item.id}
                  onClick={() => vm.setCheckedFolder(item)}
                ></CheckItem>
              ))}
            </ScrollArea>
          </>
        }
        confirmText="Confirm"
        onConfirm={() => {
          vm.onMoveConfirm(folderId);
        }}
      />
      <DecryptContractModal
        onClose={() => {
          vm.setPassWordModalOpen(false);
        }}
        encryptedContractPassword={currentContract?.encryptedContractPassword}
        user={user}
        open={passwordModalOpen}
        onOpenChange={(v) => {
          vm.setPassWordModalOpen(v);
        }}
        id={currentContract.id}
        hidePasswordInput={passwordModalType === 'view'}
        onConfirm={vm.onDecryptConfirm}
      />
      <DetailModal
        showPassword={vm.showPassword}
        password={password}
        contract={currentContract as ContractItem}
        open={openDetailModal}
        onOpenChange={vm.setOpenDetailModal}
      ></DetailModal>
      <ShareContractModal
        type={shareType}
        open={shareModalOpen}
        password={password}
        onOpenChange={vm.setShareModalOpen}
        contractId={currentContract?.id}
      />
      <InviteViewersModal
        contractId={currentContract?.id}
        open={openInviteViewersModal}
        onOpenChange={vm.setInviteViewersModal}
      />
      <Link to="/prepare/ethsign"></Link>
    </div>
  );
};

export default Page;
